import { MedicalCondition } from '../types/medical';

// Enhanced medical conditions database with comprehensive coverage
export const medicalConditions: MedicalCondition[] = [
  // Diabetes and Metabolic Disorders
  {
    id: 'diabetes-type-2',
    name: 'Type 2 Diabetes Mellitus',
    category: 'Endocrine',
    icd10Code: 'E11',
    prevalence: 'very-common',
    description: 'A chronic condition affecting how your body metabolizes glucose, the body\'s main source of energy.',
    symptoms: ['Increased thirst', 'Frequent urination', 'Increased hunger', 'Unintended weight loss', 'Fatigue', 'Blurred vision'],
    treatments: ['Metformin', 'Lifestyle changes', 'Blood glucose monitoring', 'Dietary management'],
    riskFactors: ['Obesity', 'Family history', 'Physical inactivity', 'Age over 45', 'Gestational diabetes history']
  },
  {
    id: 'diabetes-type-1',
    name: 'Type 1 Diabetes Mellitus',
    category: 'Endocrine',
    icd10Code: 'E10',
    prevalence: 'common',
    description: 'An autoimmune condition where the pancreas produces little or no insulin.',
    symptoms: ['Extreme thirst', 'Frequent urination', 'Weight loss', 'Fatigue', 'Blurred vision'],
    treatments: ['Insulin therapy', 'Blood glucose monitoring', 'Carbohydrate counting', 'Regular exercise'],
    riskFactors: ['Family history', 'Genetic factors', 'Environmental triggers', 'Age under 30']
  },
  {
    id: 'prediabetes',
    name: 'Prediabetes',
    category: 'Endocrine',
    icd10Code: 'R73.9',
    prevalence: 'very-common',
    description: 'Blood sugar levels higher than normal but not high enough for diabetes diagnosis.',
    symptoms: ['Often asymptomatic', 'Darkened skin in certain areas', 'Increased thirst', 'Frequent urination'],
    treatments: ['Lifestyle changes', 'Weight loss', 'Regular exercise', 'Healthy diet'],
    riskFactors: ['Obesity', 'Family history', 'Age over 45', 'Physical inactivity', 'Gestational diabetes history']
  },

  // Cardiovascular Conditions
  {
    id: 'hypertension',
    name: 'Hypertension (High Blood Pressure)',
    category: 'Cardiovascular',
    icd10Code: 'I10',
    prevalence: 'very-common',
    description: 'A condition in which the force of blood against artery walls is too high.',
    symptoms: ['Often asymptomatic', 'Headaches', 'Shortness of breath', 'Nosebleeds', 'Chest pain'],
    treatments: ['Lifestyle changes', 'Medication (ACE inhibitors, diuretics)', 'Regular monitoring', 'Stress management'],
    riskFactors: ['Age', 'Family history', 'Obesity', 'Physical inactivity', 'High salt diet', 'Alcohol consumption']
  },
  {
    id: 'coronary-artery-disease',
    name: 'Coronary Artery Disease',
    category: 'Cardiovascular',
    icd10Code: 'I25.1',
    prevalence: 'common',
    description: 'A narrowing of the coronary arteries that supply blood to the heart.',
    symptoms: ['Chest pain (angina)', 'Shortness of breath', 'Fatigue', 'Nausea', 'Sweating'],
    treatments: ['Medication', 'Lifestyle changes', 'Angioplasty', 'Coronary bypass surgery'],
    riskFactors: ['High blood pressure', 'High cholesterol', 'Smoking', 'Diabetes', 'Obesity', 'Physical inactivity']
  },
  {
    id: 'heart-failure',
    name: 'Heart Failure',
    category: 'Cardiovascular',
    icd10Code: 'I50',
    prevalence: 'common',
    description: 'A condition where the heart cannot pump enough blood to meet the body\'s needs.',
    symptoms: ['Shortness of breath', 'Fatigue', 'Swelling in legs/ankles', 'Rapid heartbeat', 'Chest pain'],
    treatments: ['Medication', 'Lifestyle changes', 'Device therapy', 'Surgery'],
    riskFactors: ['Coronary artery disease', 'High blood pressure', 'Diabetes', 'Obesity', 'Smoking']
  },

  // Respiratory Conditions
  {
    id: 'asthma',
    name: 'Asthma',
    category: 'Respiratory',
    icd10Code: 'J45',
    prevalence: 'very-common',
    description: 'A condition that affects the airways in the lungs, causing inflammation and narrowing.',
    symptoms: ['Wheezing', 'Shortness of breath', 'Chest tightness', 'Coughing', 'Difficulty breathing'],
    treatments: ['Inhaled medications', 'Avoiding triggers', 'Regular monitoring', 'Action plan'],
    riskFactors: ['Family history', 'Allergies', 'Environmental factors', 'Respiratory infections', 'Exercise']
  },
  {
    id: 'copd',
    name: 'Chronic Obstructive Pulmonary Disease (COPD)',
    category: 'Respiratory',
    icd10Code: 'J44',
    prevalence: 'common',
    description: 'A group of lung diseases that block airflow and make breathing difficult.',
    symptoms: ['Shortness of breath', 'Chronic cough', 'Wheezing', 'Chest tightness', 'Fatigue'],
    treatments: ['Bronchodilators', 'Inhaled steroids', 'Pulmonary rehabilitation', 'Oxygen therapy'],
    riskFactors: ['Smoking', 'Long-term exposure to lung irritants', 'Age', 'Genetic factors']
  },

  // Mental Health Conditions
  {
    id: 'depression',
    name: 'Major Depressive Disorder',
    category: 'Mental Health',
    icd10Code: 'F32',
    prevalence: 'very-common',
    description: 'A mood disorder that causes persistent feelings of sadness and loss of interest.',
    symptoms: ['Persistent sadness', 'Loss of interest', 'Changes in appetite', 'Sleep problems', 'Fatigue', 'Feelings of worthlessness'],
    treatments: ['Psychotherapy', 'Antidepressants', 'Lifestyle changes', 'Support groups'],
    riskFactors: ['Family history', 'Life events', 'Medical conditions', 'Substance abuse', 'Personality traits']
  },
  {
    id: 'anxiety',
    name: 'Generalized Anxiety Disorder',
    category: 'Mental Health',
    icd10Code: 'F41.1',
    prevalence: 'very-common',
    description: 'Excessive anxiety and worry about various aspects of life.',
    symptoms: ['Excessive worry', 'Restlessness', 'Difficulty concentrating', 'Irritability', 'Sleep problems'],
    treatments: ['Cognitive behavioral therapy', 'Medication', 'Relaxation techniques', 'Lifestyle changes'],
    riskFactors: ['Family history', 'Trauma', 'Stress', 'Medical conditions', 'Substance abuse']
  },

  // Gastrointestinal Conditions
  {
    id: 'ibs',
    name: 'Irritable Bowel Syndrome (IBS)',
    category: 'Gastrointestinal',
    icd10Code: 'K58',
    prevalence: 'common',
    description: 'A disorder affecting the large intestine, causing abdominal pain and changes in bowel habits.',
    symptoms: ['Abdominal pain', 'Bloating', 'Diarrhea or constipation', 'Mucus in stool', 'Gas'],
    treatments: ['Dietary changes', 'Stress management', 'Medication', 'Probiotics'],
    riskFactors: ['Food sensitivities', 'Stress', 'Hormonal changes', 'Bacterial overgrowth']
  },
  {
    id: 'gerd',
    name: 'Gastroesophageal Reflux Disease (GERD)',
    category: 'Gastrointestinal',
    icd10Code: 'K21',
    prevalence: 'common',
    description: 'A chronic digestive disorder where stomach acid flows back into the esophagus.',
    symptoms: ['Heartburn', 'Regurgitation', 'Chest pain', 'Difficulty swallowing', 'Sour taste'],
    treatments: ['Lifestyle changes', 'Medication', 'Dietary modifications', 'Weight management'],
    riskFactors: ['Obesity', 'Pregnancy', 'Hiatal hernia', 'Smoking', 'Certain foods']
  },

  // Musculoskeletal Conditions
  {
    id: 'arthritis',
    name: 'Osteoarthritis',
    category: 'Musculoskeletal',
    icd10Code: 'M15',
    prevalence: 'very-common',
    description: 'A degenerative joint disease causing pain and stiffness in joints.',
    symptoms: ['Joint pain', 'Stiffness', 'Reduced range of motion', 'Swelling', 'Crepitus'],
    treatments: ['Exercise', 'Weight management', 'Medication', 'Physical therapy', 'Joint replacement'],
    riskFactors: ['Age', 'Obesity', 'Joint injury', 'Repetitive stress', 'Genetic factors']
  },
  {
    id: 'fibromyalgia',
    name: 'Fibromyalgia',
    category: 'Musculoskeletal',
    icd10Code: 'M79.7',
    prevalence: 'common',
    description: 'A disorder characterized by widespread musculoskeletal pain and fatigue.',
    symptoms: ['Widespread pain', 'Fatigue', 'Sleep problems', 'Cognitive difficulties', 'Stiffness'],
    treatments: ['Medication', 'Exercise', 'Stress management', 'Sleep hygiene', 'Cognitive behavioral therapy'],
    riskFactors: ['Gender (more common in women)', 'Family history', 'Rheumatic diseases', 'Trauma']
  },

  // Neurological Conditions
  {
    id: 'migraine',
    name: 'Migraine',
    category: 'Neurological',
    icd10Code: 'G43',
    prevalence: 'common',
    description: 'A neurological condition characterized by recurrent, severe headaches.',
    symptoms: ['Severe headache', 'Nausea', 'Sensitivity to light/sound', 'Aura', 'Vomiting'],
    treatments: ['Pain medication', 'Preventive medication', 'Lifestyle changes', 'Trigger avoidance'],
    riskFactors: ['Family history', 'Gender (more common in women)', 'Age', 'Hormonal changes', 'Stress']
  },
  {
    id: 'epilepsy',
    name: 'Epilepsy',
    category: 'Neurological',
    icd10Code: 'G40',
    prevalence: 'uncommon',
    description: 'A neurological disorder characterized by recurrent seizures.',
    symptoms: ['Seizures', 'Loss of consciousness', 'Uncontrollable movements', 'Sensory changes'],
    treatments: ['Antiepileptic medication', 'Surgery', 'Vagus nerve stimulation', 'Ketogenic diet'],
    riskFactors: ['Brain injury', 'Stroke', 'Brain tumors', 'Infections', 'Genetic factors']
  },

  // Autoimmune Conditions
  {
    id: 'rheumatoid-arthritis',
    name: 'Rheumatoid Arthritis',
    category: 'Autoimmune',
    icd10Code: 'M06',
    prevalence: 'common',
    description: 'An autoimmune disorder causing chronic inflammation of joints.',
    symptoms: ['Joint pain and swelling', 'Morning stiffness', 'Fatigue', 'Fever', 'Weight loss'],
    treatments: ['Disease-modifying drugs', 'Biologics', 'Physical therapy', 'Surgery'],
    riskFactors: ['Gender (more common in women)', 'Age', 'Family history', 'Smoking', 'Obesity']
  },
  {
    id: 'lupus',
    name: 'Systemic Lupus Erythematosus',
    category: 'Autoimmune',
    icd10Code: 'M32',
    prevalence: 'uncommon',
    description: 'An autoimmune disease affecting multiple organs and systems.',
    symptoms: ['Fatigue', 'Joint pain', 'Skin rashes', 'Fever', 'Hair loss', 'Raynaud\'s phenomenon'],
    treatments: ['Corticosteroids', 'Immunosuppressants', 'Antimalarials', 'Lifestyle changes'],
    riskFactors: ['Gender (more common in women)', 'Age', 'Family history', 'Ethnicity', 'Environmental factors']
  },

  // Thyroid Conditions
  {
    id: 'hypothyroidism',
    name: 'Hypothyroidism',
    category: 'Endocrine',
    icd10Code: 'E03',
    prevalence: 'common',
    description: 'An underactive thyroid gland that doesn\'t produce enough thyroid hormone.',
    symptoms: ['Fatigue', 'Weight gain', 'Cold intolerance', 'Dry skin', 'Hair loss', 'Depression'],
    treatments: ['Thyroid hormone replacement', 'Regular monitoring', 'Lifestyle changes'],
    riskFactors: ['Age', 'Gender (more common in women)', 'Family history', 'Autoimmune diseases', 'Radiation exposure']
  },
  {
    id: 'hyperthyroidism',
    name: 'Hyperthyroidism',
    category: 'Endocrine',
    icd10Code: 'E05',
    prevalence: 'common',
    description: 'An overactive thyroid gland that produces too much thyroid hormone.',
    symptoms: ['Weight loss', 'Rapid heartbeat', 'Nervousness', 'Tremors', 'Heat intolerance', 'Insomnia'],
    treatments: ['Antithyroid medication', 'Radioactive iodine', 'Surgery', 'Beta blockers'],
    riskFactors: ['Gender (more common in women)', 'Age', 'Family history', 'Autoimmune diseases']
  },

  // Sleep Disorders
  {
    id: 'sleep-apnea',
    name: 'Sleep Apnea',
    category: 'Sleep',
    icd10Code: 'G47.3',
    prevalence: 'common',
    description: 'A serious sleep disorder where breathing repeatedly stops and starts during sleep.',
    symptoms: ['Loud snoring', 'Episodes of stopped breathing', 'Gasping for air', 'Excessive daytime sleepiness', 'Morning headaches'],
    treatments: ['CPAP therapy', 'Lifestyle changes', 'Oral appliances', 'Surgery'],
    riskFactors: ['Obesity', 'Age', 'Gender (more common in men)', 'Family history', 'Smoking', 'Alcohol use']
  },
  {
    id: 'insomnia',
    name: 'Insomnia',
    category: 'Sleep',
    icd10Code: 'F51.0',
    prevalence: 'very-common',
    description: 'A sleep disorder characterized by difficulty falling or staying asleep.',
    symptoms: ['Difficulty falling asleep', 'Waking up frequently', 'Early morning awakening', 'Daytime fatigue', 'Irritability'],
    treatments: ['Cognitive behavioral therapy', 'Sleep hygiene', 'Medication', 'Relaxation techniques'],
    riskFactors: ['Stress', 'Anxiety', 'Depression', 'Medical conditions', 'Medications', 'Caffeine']
  }
];

// Enhanced condition categories with descriptions
export const conditionCategories = [
  { id: 'all', name: 'All Conditions', description: 'Browse all available health conditions' },
  { id: 'Endocrine', name: 'Endocrine & Metabolic', description: 'Conditions affecting hormones and metabolism' },
  { id: 'Cardiovascular', name: 'Cardiovascular', description: 'Heart and blood vessel conditions' },
  { id: 'Respiratory', name: 'Respiratory', description: 'Lung and breathing conditions' },
  { id: 'Mental Health', name: 'Mental Health', description: 'Psychological and emotional conditions' },
  { id: 'Gastrointestinal', name: 'Gastrointestinal', description: 'Digestive system conditions' },
  { id: 'Musculoskeletal', name: 'Musculoskeletal', description: 'Bone, joint, and muscle conditions' },
  { id: 'Neurological', name: 'Neurological', description: 'Brain and nervous system conditions' },
  { id: 'Autoimmune', name: 'Autoimmune', description: 'Conditions where the immune system attacks the body' },
  { id: 'Sleep', name: 'Sleep Disorders', description: 'Conditions affecting sleep patterns and quality' }
];

// Enhanced search function with better matching
export function searchConditions(query: string): MedicalCondition[] {
  if (!query.trim()) {
    return medicalConditions;
  }

  const searchTerm = query.toLowerCase();
  
  return medicalConditions.filter(condition => {
    // Search in name, description, symptoms, and treatments
    const searchableText = [
      condition.name,
      condition.description,
      condition.icd10Code,
      condition.category,
      ...(condition.symptoms || []),
      ...(condition.treatments || []),
      ...(condition.riskFactors || [])
    ].join(' ').toLowerCase();

    return searchableText.includes(searchTerm);
  });
}

// Get conditions by category
export function getConditionsByCategory(category: string): MedicalCondition[] {
  if (category === 'all') {
    return medicalConditions;
  }
  return medicalConditions.filter(condition => condition.category === category);
}

// Get conditions by prevalence
export function getConditionsByPrevalence(prevalence: string): MedicalCondition[] {
  return medicalConditions.filter(condition => condition.prevalence === prevalence);
}

// Get common conditions (very-common prevalence)
export function getCommonConditions(): MedicalCondition[] {
  return getConditionsByPrevalence('very-common');
}